import React from 'react';
import { MessageInfo, MessageType, GroupUpdateType } from '@/api/types';

interface SystemMessageProps {
  message: MessageInfo;
}

/**
 * 系统消息组件 - 处理群组管理等系统消息
 */
const SystemMessage: React.FC<SystemMessageProps> = ({ message }) => {
  const contentData = message.decryptedContentData;
  
  const jsonData = JSON.parse(contentData?.content || '{}');
  if (contentData) {
    contentData.invite_name = jsonData.invite_name;
    contentData.member_names = jsonData.member_names;
    contentData.member_handles = jsonData.member_handles;
    contentData.group_update_type = jsonData.group_update_type;
    contentData.group_name = jsonData.group_name;
    contentData.group_avatar = jsonData.group_avatar;
    contentData.group_color = jsonData.group_color;
    contentData.permissions = jsonData.permissions;
    contentData.notice = jsonData.notice;
    contentData.message_id = jsonData.message_id;
    contentData.message_content = jsonData.message_content;
    contentData.sender_id = jsonData.sender_id;
    contentData.sender_name = jsonData.sender_name;
  }
  
  // 根据消息类型生成显示文本
  const getSystemMessageText = (): string => {
    if (!contentData) return message.content;
    
    const messageType = message.messageType as MessageType;
    const senderName = contentData.sender_name || contentData.senderName || '某人';
    
    switch (messageType) {
      // 群组管理消息
      case MessageType.GROUP_NEW_CREATE:
        return `${senderName} 创建了群聊`;
        
      case MessageType.GROUP_NEW_INVITE:
        const inviteNames = contentData.member_names?.join('、') || '新成员';
        return `${contentData.invite_name || senderName} 邀请 ${inviteNames} 加入群聊`;
        
      case MessageType.GROUP_MEMBER_ADDED:
        const addedNames = contentData.member_names?.join('、') || '新成员';
        return `${contentData.invite_name || senderName} 将 ${addedNames} 添加到群聊`;
        
      case MessageType.GROUP_MEMBER_REMOVED:
        const removedNames = contentData.member_names?.join('、') || '成员';
        return `${senderName} 将 ${removedNames} 移出群聊`;
        
      case MessageType.GROUP_MEMBER_LEAVE:
        return `${senderName} 退出了群聊`;
        
      case MessageType.GROUP_DISSOLVE:
        return `${senderName} 解散了群聊`;
        
      // 群组信息更新
      case MessageType.GROUP_INFO_UPDATE:
        const updateType = contentData.group_update_type;
        switch (updateType) {
          case GroupUpdateType.NAME:
            return `${senderName} 修改群名称为 "${contentData.group_name}"`;
          case GroupUpdateType.AVATAR:
            return `${senderName} 修改了群头像`;
          case GroupUpdateType.COLOR:
            return `${senderName} 修改了群颜色`;
          default:
            return `${senderName} 修改了群信息`;
        }
        
      case MessageType.GROUP_NOTICE_ADD:
        return `${senderName} 发布了群公告`;
        
      // 群组权限管理
      case MessageType.GROUP_MUTE_ENABLED:
        return `${senderName} 开启了全员禁言`;
        
      case MessageType.GROUP_MUTE_DISABLED:
        return `${senderName} 关闭了全员禁言`;
        
      case MessageType.GROUP_SCREENSHOT_ENABLED:
        return `${senderName} 允许群成员截屏`;
        
      case MessageType.GROUP_SCREENSHOT_DISABLED:
        return `${senderName} 禁止群成员截屏`;
        
      // 群组发送权限
      case MessageType.GROUP_SEND_CARD_ENABLED:
        return `${senderName} 允许群成员发送名片`;
        
      case MessageType.GROUP_SEND_CARD_DISABLED:
        return `${senderName} 禁止群成员发送名片`;
        
      case MessageType.GROUP_SEND_IMAGE_ENABLED:
        return `${senderName} 允许群成员发送图片/视频`;
        
      case MessageType.GROUP_SEND_IMAGE_DISABLED:
        return `${senderName} 禁止群成员发送图片/视频`;
        
      case MessageType.GROUP_SEND_VIDEO_ENABLED:
        return `${senderName} 允许群成员发送视频`;
        
      case MessageType.GROUP_SEND_VIDEO_DISABLED:
        return `${senderName} 禁止群成员发送视频`;
        
      case MessageType.GROUP_SEND_FILE_ENABLED:
        return `${senderName} 允许群成员发送文件`;
        
      case MessageType.GROUP_SEND_FILE_DISABLED:
        return `${senderName} 禁止群成员发送文件`;
        
      // 置顶消息
      case MessageType.GROUP_PINNED_MESSAGE_ADD:
        return `${senderName} 置顶了一条消息`;
        
      case MessageType.GROUP_PINNED_MESSAGE_DELETE:
        return `${senderName} 取消置顶消息`;
        
      default:
        return message.content || '系统消息';
    }
  };

  // 获取系统消息图标
  const getSystemIcon = (): string => {
    const messageType = message.messageType as MessageType;
    
    switch (messageType) {
      case MessageType.GROUP_NEW_CREATE:
      case MessageType.GROUP_NEW_INVITE:
      case MessageType.GROUP_MEMBER_ADDED:
        return '👥';
        
      case MessageType.GROUP_MEMBER_REMOVED:
      case MessageType.GROUP_MEMBER_LEAVE:
        return '👋';
        
      case MessageType.GROUP_DISSOLVE:
        return '💔';
        
      case MessageType.GROUP_INFO_UPDATE:
        return '✏️';
        
      case MessageType.GROUP_NOTICE_ADD:
        return '📢';
        
      case MessageType.GROUP_MUTE_ENABLED:
      case MessageType.GROUP_MUTE_DISABLED:
        return '🔇';
        
      case MessageType.GROUP_SCREENSHOT_ENABLED:
      case MessageType.GROUP_SCREENSHOT_DISABLED:
        return '📸';
        
      case MessageType.GROUP_SEND_CARD_ENABLED:
      case MessageType.GROUP_SEND_CARD_DISABLED:
        return '👤';
        
      case MessageType.GROUP_SEND_IMAGE_ENABLED:
      case MessageType.GROUP_SEND_IMAGE_DISABLED:
        return '🖼️';
        
      case MessageType.GROUP_SEND_VIDEO_ENABLED:
      case MessageType.GROUP_SEND_VIDEO_DISABLED:
        return '🎥';
        
      case MessageType.GROUP_SEND_FILE_ENABLED:
      case MessageType.GROUP_SEND_FILE_DISABLED:
        return '📎';
        
      case MessageType.GROUP_PINNED_MESSAGE_ADD:
      case MessageType.GROUP_PINNED_MESSAGE_DELETE:
        return '📌';
        
      default:
        return 'ℹ️';
    }
  };

  return (
    <div className="flex justify-center my-2">
      <div className="flex items-center px-3 py-2 bg-gray-100 rounded-[20px] max-w-md">
        <span className="text-sm mr-2">{getSystemIcon()}</span>
        <span className="text-xs text-gray-600 text-center">
          {getSystemMessageText()}
        </span>
      </div>
    </div>
  );
};

export default SystemMessage;
